import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const HOWTO_02: GgxTourDefinition = {
    key: '[HOWTO-02] Live Testing a pipeline',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 3,
    prerequisite: {
        target: '[test-id="search-button"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Live Testing a Pipeline',
        subtitle:
            'Before a full evaluation, you can chat with a pipeline directly to check it ' +
            'behaves as expected. This tour opens a chat-based pipeline, sends it a query, ' +
            'rates the reply, and saves the run for later.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You have found a pipeline with the global search, started a chat session, tested ' +
            'it as an end user, given feedback, and saved the transcript. Next, run a ' +
            'structured evaluation to measure specific types of risk.',
        learned: [
            'A **chat session** lets you test a pipeline as an end user.',
            '**Rating a reply** records feedback the builders can see.',
            '**Saving the session** keeps the transcript for later.',
        ],
    },
    chapters: [
        {
            name: 'Open the pipeline',
            blurb: 'Search from anywhere and jump straight to it',
            icon: 'map',
            steps: [2, 3, 4],
        },
        {
            name: 'Start a chat',
            blurb: 'Open a session and pick a persona',
            icon: 'compass',
            steps: [5, 6, 7],
        },
        {
            name: 'Test it',
            blurb: 'Send a message and rate the reply',
            icon: 'pulse',
            steps: [8, 9],
        },
        {
            name: 'Save the session',
            blurb: 'End, save and export the run',
            icon: 'list',
            steps: [10, 11, 12],
        },
    ],
    steps: [
        {
            target: '[test-id="search-button"]',
            title: "Let's open the pipeline we will chat with",
            whatHappens: 'a search box opens in the middle of the screen.',
            icon: 'search',
            description:
                'We will use <strong>Combined Customer Intent and Response Pipeline</strong>, ' +
                'a customer service chatbot for credit card questions. ' +
                'The quickest way to it is the global search. It finds pipelines, prompts, ' +
                'reports and portals by name from any page, and <strong>Ctrl+K</strong> opens ' +
                'it too. Click <strong>Search</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
            actionLabel: 'Search',
        },
        {
            target: '[test-id="global-search"] input',
            title: 'Type the name of the chatbot',
            whatHappens:
                'matching objects appear below the box as you type, and the tour moves on when ' +
                'the pipeline is among them.',
            icon: 'search',
            description:
                'Click the tag to copy the name, paste it into the search box, and watch the ' +
                'results narrow down.<br><br>' +
                '<code data-ggx-copy>Combined Customer Intent and Response Pipeline</code>',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetMount: true,
            waitCue: 'Type the name to continue',
        },
        {
            target: '[test-id^="global-search-option-"]',
            targetText: 'Combined Customer Intent and Response Pipeline v1',
            scrollIntoView: true,
            title: 'Pick the pipeline itself',
            whatHappens: "the pipeline's page opens, with its tabs across the top.",
            icon: 'search',
            description:
                'A few objects share this name. The one we want is the pipeline marked ' +
                '<strong>v1</strong>. Click <strong>Combined Customer Intent and Response ' +
                'Pipeline</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Combined Customer Intent and Response Pipeline',
        },
        {
            // Measured: tab strip at y=102, x=427. Plenty of room below.
            target: '[test-id="tab-item-Chats"]',
            scrollIntoView: true,
            title: 'Chats is where you talk to it',
            whatHappens: "the Chats tab opens with this pipeline's past sessions.",
            icon: 'compass',
            description:
                'Before you run a big evaluation, it helps to simply talk to the pipeline and ' +
                'see how it answers. Every chat-based pipeline has a Chats tab for that, and it ' +
                'keeps each conversation so you can come back to it. Click ' +
                '<strong>Chats</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            // Measured: x=1870 in a 2048 viewport, so the popover goes to its left.
            target: '[test-id="start-chat"]',
            title: "Let's start a new session",
            whatHappens: 'a fresh chat window opens on the right.',
            icon: 'pulse',
            description:
                'A session is one conversation with the pipeline. Each one is recorded ' +
                'separately, so you can test a scenario, keep the transcript and share it with a ' +
                'teammate later. Click <strong>Start Chat</strong>.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="persona-Henry Adams"]',
            title: 'Who is the bot talking to?',
            whatHappens: "the bot answers using the selected persona's mock account data.",
            icon: 'grid',
            description:
                'A customer service bot needs to know whose account it is looking at. Personas ' +
                'are sample customers with mock account details, card activity and recent ' +
                'interactions, covering everyday cases like lost cards, fraud disputes and ' +
                'travel. <strong>Henry Adams</strong> is already selected, which is fine for ' +
                'this test. You can switch persona here if you like, then click Continue.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next', 'close'],
        },
        {
            // Measured: textarea at y=642, 979px wide. Popover sits above it.
            target: '[test-id="message-input"]',
            title: 'Ask it something a customer would ask',
            whatHappens:
                'the bot replies below your message, and the tour moves on by itself once the ' +
                'answer arrives.',
            icon: 'pulse',
            description:
                'This is where you type as if you were the customer. Try the question below: it ' +
                "asks about a balance, which the bot answers by looking up Henry's account. " +
                'Click the tag to copy it, paste it into the message box and press ' +
                '<strong>Enter</strong>.<br><br><code data-ggx-copy>What is the balance on my ' +
                'credit card ending in 9010?</code>',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetMount: true,
            waitCue: 'Send your message to continue',
        },
        {
            target: '[test-id="transcript-rating"]',
            title: 'How did it do?',
            whatHappens: 'your rating is stored with this reply for the team to review.',
            icon: 'grid',
            description:
                'Under every reply you can give a <strong>Thumbs Up</strong> or <strong>Thumbs ' +
                'Down</strong>. This is how testers tell the team which answers were good and ' +
                'which need work. A thumbs down asks you for a reason and a comment, and ' +
                'everything is saved automatically with the session. Rate the reply if you like, ' +
                'then click Continue.',
            side: 'bottom',
            align: 'start',
        },
        {
            // Verified in the running app: the chat panel header's close control, at
            // x=2003 y=7. It carries no test-id, so it is matched by its PrimeNG icon.
            // The header also holds pi-ellipsis-v, pi-angle-left and pi-angle-right.
            target: '[test-id="chat-session-close"]',
            title: 'Wrapping up a session',
            whatHappens:
                'closing the session asks for your notes and saves them with the transcript.',
            icon: 'list',
            description:
                'Everything you did in this chat is already saved. When you finish testing, this ' +
                'close button ends the session and lets you write down your observations, so a ' +
                'developer can pick them up and fix what you found. We will leave it open for ' +
                'now and look at the export first, so click Continue.',
            side: 'left',
            align: 'start',
        },
        {
            // Verified: two pi-ellipsis-v buttons exist and both sit under a sticky
            // ancestor. Only the chat panel's is a direct child of the header's
            // items-center row; the other at y=60 belongs to the page behind it.
            target: '[test-id="chat-session-options"]',
            title: 'Want to keep a copy?',
            whatHappens: 'a small menu opens with the download option.',
            icon: 'compass',
            description:
                'Sometimes you need the conversation outside GGX, for a ticket, a review or your ' +
                'own analysis. The options menu on the chat panel lets you download it. Click ' +
                'the <strong>three dots</strong> in the top right of the chat.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            // Verified: the menu renders as a p-popover; the item is a button at
            // (1744, 123), 180x34, matched by its label.
            target: '[test-id="download-transcript"]',
            title: 'Take the transcript with you',
            whatHappens: 'the file downloads to your computer.',
            icon: 'list',
            description:
                'The transcript is the whole conversation as a text file, with your questions ' +
                "and the bot's replies. Attach it to a ticket, share it with your team or keep " +
                'it as evidence that the test was done. Click <strong>Download ' +
                'Transcript</strong>.',
            side: 'left',
            align: 'center',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
    ],
};
